const IMAGE_FILE_TYPES = [
    'jpeg',
    'jpg',
    'png',
    'svg',
    'webp',
    'gif',
    'jfif',
    'pjpeg',
    'pjp',
    'apng',
    'avif',
    'tif',
    'tiff',
    'heif',
    'bmp',
    'eps',
    'raw',
    'cr2',
    'nef',
    'orf',
    'sr2',
    'psd',
    'heic',
    'dib',
    'ai',
];

const DEFAULT_ASPECT_RATIO = 0.7041;


export const customImageConfig = {
    name: 'Image', // must match the name in the builder.io model
    static: true,
    image:
        'https://firebasestorage.googleapis.com/v0/b/builder-3b0a2.appspot.com/o/images%2Fbaseline-insert_photo-24px.svg?alt=media&token=4e5d0ef4-f5e8-4e57-b3a9-38d63a9b9dc4',
    defaultStyles: {
        position: 'relative',
        minHeight: '20px',
        minWidth: '20px',
        overflow: 'hidden',
    },
    canHaveChildren: true,
    inputs: [
        {
            // TODO: new editor type 'responsiveImage' that can do different crops per breakpoint
            // and sets an object and that is read here
            name: 'image',
            type: 'file',
            bubble: true,
            allowedFileTypes: IMAGE_FILE_TYPES,
            required: true,
            defaultValue:
                'https://cdn.builder.io/api/v1/image/assets%2FYJIGb4i01jvw0SRdL5Bt%2F72c80f114dc149019051b6852a9e3b7a',
            onChange: (options: Map<string, any>) => {
                const DEFAULT_ASPECT_RATIO = 0.7041;
                options.delete('srcset');
                options.delete('noWebp');
                function loadImage(url: string, timeout = 60000): Promise<HTMLImageElement> {
                    return new Promise((resolve, reject) => {
                        const img = document.createElement('img');
                        let loaded = false;
                        img.onload = () => {
                            loaded = true;
                            resolve(img);
                        };

                        img.addEventListener('error', event => {
                            console.warn('Image load failed', event.error);
                            reject(event.error);
                        });

                        img.src = url;
                        setTimeout(() => {
                            if (!loaded) {
                                reject(new Error('Image load timed out'));
                            }
                        }, timeout);
                    });
                }

                function round(num: number) {
                    return Math.round(num * 1000) / 1000;
                }

                const value = options.get('image');
                const aspectRatio = options.get('aspectRatio');

                // For SVG images - don't render as webp, keep them as SVG
                fetch(value)
                    .then(res => res.blob())
                    .then(blob => {
                        if (blob.type.includes('svg')) {
                            options.set('noWebp', true);
                        }
                    });

                if (value && (!aspectRatio || aspectRatio === DEFAULT_ASPECT_RATIO)) {
                    return loadImage(value).then(img => {
                        const possiblyUpdatedAspectRatio = options.get('aspectRatio');
                        if (
                            options.get('image') === value &&
                            (!possiblyUpdatedAspectRatio || possiblyUpdatedAspectRatio === DEFAULT_ASPECT_RATIO)
                        ) {
                            if (img.width && img.height) {
                                options.set('aspectRatio', round(img.height / img.width));
                                options.set('height', img.height);
                                options.set('width', img.width);
                            }
                        }
                    });
                }
            },
        },
        {
            name: 'backgroundSize',
            type: 'text',
            defaultValue: 'cover',
            enum: [
                {
                    label: 'contain',
                    value: 'contain',
                    helperText: 'The image should never get cropped',
                },
                {
                    label: 'cover',
                    value: 'cover',
                    helperText: `The image should fill its box, cropping when needed`,
                },
                // TODO: add these options back
                // { label: 'auto', value: 'auto', helperText: '' },
                // { label: 'fill', value: 'fill', helperText: 'The image should fill the box, being stretched or squished if necessary' },
            ] as any,
        },
        {
            name: 'backgroundPosition',
            type: 'text',
            defaultValue: 'center',
            enum: [
                'center',
                'top',
                'left',
                'right',
                'bottom',
                'top left',
                'top right',
                'bottom left',
                'bottom right',
            ],
        },
        {
            name: 'altText',
            type: 'string',
            helperText: 'Text to display when the user has images off',
        },
        {
            name: 'title',
            type: 'string',
            helperText: 'Text to display when hovering over the asset',
        },
        // TODO: force lazy load option (maybe via binding for now hm component.options.lazy: true)
        {
            name: 'lazy',
            type: 'boolean',
            advanced: true,
            defaultValue: true,
            helperText:
                'Lazy loading should be set to false for above-the-fold images.',
        },
        {
            name: 'fetchPriority',
            type: 'text',
            enum: ['high', 'low', 'auto'],
            advanced: true,
            defaultValue: 'auto',
            showIf: (options: any) => options.get('lazy') === false,
            helperText: 'HIGH: Hero/LCP images. LOW: Decorative images. AUTO: Browser default (recommended).'
        },
        {
            name: 'height',
            type: 'number',
            hideFromUI: true,
        },
        {
            name: 'width',
            type: 'number',
            hideFromUI: true,
        },
        {
            name: 'sizes',
            type: 'string',
            hideFromUI: true,
        },
        {
            name: 'srcset',
            type: 'string',
            hideFromUI: true,
        },
        {
            name: 'fitContent',
            type: 'boolean',
            helperText:
                "When child blocks are provided, fit to them instead of using the image's aspect ratio",
            defaultValue: true,
        },
        {
            name: 'aspectRatio',
            type: 'number',
            helperText:
                "This is the ratio of height/width, e.g. set to 1.5 for a 300px wide and 200px tall photo. Set to 0 to not force the image to maintain it's aspect ratio",
            advanced: true,
            defaultValue: DEFAULT_ASPECT_RATIO,
        },
        // {
        //   name: 'backgroundRepeat',
        //   type: 'text',
        //   defaultValue: 'no-repeat',
        //   enum: ['no-repeat', 'repeat', 'repeat-x', 'repeat-y'],
        // },
    ],
};